import React from 'react';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { Box, Card, Divider, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CurrencyAmount } from '@cowbell/ui/core';

import CbButton from '../../../components/Buttons/CbButton';

import { approveQuote } from '../../../inbox/QuotesService';
import { REDACTED_FIELD } from '../../../config/logrocket';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';

export const GenericApproveQuote = ({ data = {}, ...props }) => {
  // @ts-expect-error TS(2339): Property 'formattedQuote' does not exist on type '... Remove this comment to see the full error message
  const { formattedQuote: quote } = data;
  const classes = useStyles();
  const { push } = useHistory();
  const notifications = useNotifications();
  const handleAPIError = useAPIErrorHandler();

  const [isApproving, setApproving] = React.useState(false);

  const handleSubmit = () => {
    setApproving(true);
    if (_.isFunction(props.onSubmit)) {
      props.onSubmit(quote, setApproving);
    } else {
      approveQuote({ quoteId: quote.id, productType: quote.product })
        .then(() => {
          notifications.success('Quote Approved successfully.');
          if (_.isFunction(props.onMovingAhead)) {
            props.onMovingAhead();
          }
        })
        .catch((error) => {
          if (error.response.status === 400) {
            notifications.error(
              'Renewal quote(s) are still patching, please try approving again in a minute.'
            );
          } else {
            handleAPIError(
              'Something went wrong, please try again in a few minutes or contact support'
            )(error);
          }
        })
        .finally(() => {
          setApproving(false);
        });
    }
  };

  const handleCancel = () => {
    if (_.isFunction(props.onCancel)) {
      props.onCancel();
    } else {
      push('/customer/policies');
    }
  };

  return (
    <section>
        <Card>
          <Box pl={3} pr={3} pb={3}>
            <Grid container>
              <Grid item md={6}>
                <h2>Approve Quote Number {quote.quoteNumber}</h2>
                <Divider className={classes.divider} />

                <Grid container spacing={10}>
                  <Grid
                    item
                    md={6}
                    className={`${classes.details} ${classes.verticalDividingLine}`}
                    style={{ paddingTop: 0, paddingBottom: 24 }}
                  >
                    <h3 className={classes.headings}>QUOTE INFORMATION</h3>
                    <Grid container>
                      <Grid item md={6}>
                        Premium:
                      </Grid>
                      <Grid item md={6}>
                        <strong className="details-value">
                          <CurrencyAmount
                            amount={quote.totalPremium}
                            currency={quote.currency}
                          />
                        </strong>
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item md={6}>
                        Aggregate Limit:
                      </Grid>
                      <Grid item md={6}>
                        <strong className="details-value">
                          <CurrencyAmount
                            amount={quote.limit}
                            currency={quote.currency}
                            minimumFractionDigits={0}
                          />
                        </strong>
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item md={6}>
                        Deductible Amount:
                      </Grid>
                      <Grid item md={6}>
                        <strong className="details-value">
                          <CurrencyAmount
                            amount={quote.deductible}
                            currency={quote.currency}
                            minimumFractionDigits={0}
                          />
                        </strong>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    className={classes.details}
                    style={{ paddingTop: 0, paddingBottom: 24 }}
                  >
                    <h3 className={classes.headings}>
                      AGENCY/AGENT INFORMATION
                    </h3>
                    <Grid container>
                      <Grid item md={4}>
                        From Agency:
                      </Grid>
                      <Grid className={REDACTED_FIELD} item md={6}>
                        <strong className="details-value">
                          {quote.agencyName}
                        </strong>
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item md={4}>
                        By Agent:
                      </Grid>
                      <Grid className={REDACTED_FIELD} item md={6}>
                        <strong className="details-value">
                          {quote.agentFirstName} {quote.agentLastName}
                        </strong>
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item md={4}>
                        Email:
                      </Grid>
                      <Grid className={REDACTED_FIELD} item md={6}>
                        <strong className="details-value">
                          {quote.agentEmail}
                        </strong>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Divider className={classes.divider} />

                <Box p={2} pb={5} textAlign="center">
                  <CbButton
                    styleName="cancel"
                    onClick={handleCancel}
                    buttonText="Cancel"
                  />
                  <CbButton
                    loading={isApproving}
                    styleName="primary"
                    onClick={handleSubmit}
                    buttonText="Approve"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </section>
  );
};

const useStyles = makeStyles(({ palette }) => ({
  details: {
    '& .details-value': {
      color: palette.text.link,
      fontWeight: 600,
      textAlign: 'right',
    },
  },
  headings: {
    color: palette.primary.contrastText,
    marginTop: '2.5rem',
    marginBottom: 0,
    fontWeight: 400,
  },
  verticalDividingLine: {
    position: 'relative',

    '&:before': {
      content: "''",
      width: 1,
      position: 'absolute',
      height: '55%',
      top: '55%',
      right: 0,
      transform: 'translateY(-50%)',
      backgroundColor: palette.primary.border,
    },
  },
  divider: {
    marginTop: '2.5rem',
    marginBottom: '1.6666666667rem',
  },
}));
